import * as React from "react";
import { useParams } from "react-router";
import { useCurrentProject } from "@/lib/project";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, MoreHorizontal, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/components/bespoke";
import { formatDateTime } from "@/lib/format";

export type EvalCriterion = {
  id: string;
  projectId: string;
  graderId: string;
  name: string;
  description: string | null;
  threshold: Record<string, unknown>;
  weight: number;
  createdAt: string;
};

async function fetchCriteria(projectId: string, graderId?: string): Promise<EvalCriterion[]> {
  const params = graderId ? `?graderId=${graderId}` : "";
  const response = await fetch(`/api/v1/projects/${projectId}/eval/criteria${params}`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).criteria;
}

async function createCriterion(
  projectId: string,
  input: {
    graderId: string;
    name: string;
    description?: string | null;
    threshold?: Record<string, unknown>;
    weight?: number;
  },
): Promise<EvalCriterion> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/criteria`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function updateCriterion(
  projectId: string,
  criterionId: string,
  input: {
    name?: string;
    description?: string | null;
    threshold?: Record<string, unknown>;
    weight?: number;
  },
): Promise<EvalCriterion> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/criteria/${criterionId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function deleteCriterion(projectId: string, criterionId: string): Promise<void> {
  await fetch(`/api/v1/projects/${projectId}/eval/criteria/${criterionId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
}

function CriterionRow({
  criterion,
  onEdit,
  onDelete,
}: {
  criterion: EvalCriterion;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-medium">
        {criterion.name}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground max-w-xs truncate">
        {criterion.description ?? "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <pre className="text-xs font-mono text-muted-foreground max-w-xs truncate">
          {JSON.stringify(criterion.threshold)}
        </pre>
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
        {criterion.weight}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatDateTime(criterion.createdAt)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive-text">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export function EvalCriteriaPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const { graderId } = useParams<{ graderId?: string }>();
  const queryClient = useQueryClient();

  const { data: criteria, isLoading } = useQuery({
    queryKey: ["eval-criteria", projectId, graderId],
    queryFn: () => fetchCriteria(projectId!, graderId),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: {
      graderId: string;
      name: string;
      description?: string | null;
      threshold?: Record<string, unknown>;
      weight?: number;
    }) => createCriterion(projectId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-criteria", projectId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      criterionId,
      input,
    }: {
      criterionId: string;
      input: {
        name?: string;
        description?: string | null;
        threshold?: Record<string, unknown>;
        weight?: number;
      };
    }) => updateCriterion(projectId!, criterionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-criteria", projectId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (criterionId: string) => deleteCriterion(projectId!, criterionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-criteria", projectId] });
    },
  });

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editDescription, setEditDescription] = React.useState("");
  const [editThreshold, setEditThreshold] = React.useState("{}");
  const [editWeight, setEditWeight] = React.useState(1);
  const [editGraderId, setEditGraderId] = React.useState(graderId ?? "");
  const [search, setSearch] = React.useState("");

  const filteredCriteria =
    criteria?.filter(
      (c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.description?.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    try {
      const threshold = formData.get("threshold")
        ? JSON.parse(formData.get("threshold") as string)
        : {};
      createMutation.mutate({
        graderId: formData.get("graderId") as string,
        name: formData.get("name") as string,
        description: (formData.get("description") as string) || null,
        threshold,
        weight: parseFloat(formData.get("weight") as string) || 1,
      });
    } catch {
      alert("Invalid JSON in threshold");
      return;
    }
    setIsCreateOpen(false);
    e.currentTarget.reset();
  };

  const handleUpdate = (criterionId: string) => {
    try {
      const threshold = editThreshold ? JSON.parse(editThreshold) : {};
      updateMutation.mutate({
        criterionId,
        input: {
          name: editName,
          description: editDescription || null,
          threshold,
          weight: editWeight,
        },
      });
    } catch {
      alert("Invalid JSON in threshold");
      return;
    }
    setEditingId(null);
  };

  const handleDelete = (criterionId: string) => {
    if (confirm("Are you sure you want to delete this criterion?")) {
      deleteMutation.mutate(criterionId);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Criteria"
        headingLevel={headingLevel}
        description="Scoring criteria for graders — thresholds, weights, and pass conditions."
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New criterion
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search criteria..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isCreateOpen && (
            <form
              onSubmit={handleCreate}
              className="mb-4 p-4 border rounded-lg bg-muted/30 space-y-3"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Grader</label>
                  <Select
                    name="graderId"
                    value={editGraderId}
                    onValueChange={setEditGraderId}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a grader" />
                    </SelectTrigger>
                    <SelectContent>
                      {graderId && <SelectItem value={graderId}>{graderId}</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input name="name" placeholder="Accuracy" required autoFocus />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Weight</label>
                  <Input name="weight" type="number" min="0" step="0.1" defaultValue="1" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description (optional)</label>
                  <Input name="description" placeholder="What this criterion measures" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Threshold (JSON)</label>
                <textarea
                  name="threshold"
                  placeholder='{"minScore": 0.8}'
                  className="w-full p-2 text-sm font-mono border rounded-md bg-background"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create criterion"}
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Criteria</caption>
              <thead>
                <tr className="border-b">
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[25%]"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[25%]"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                  >
                    Threshold
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[10%]"
                  >
                    Weight
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[15%]"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[5%]"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading criteria...
                    </td>
                  </tr>
                ) : filteredCriteria.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <p className="font-medium">No criteria yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Create criteria to define how graders score eval runs.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredCriteria.map((criterion) => (
                    <CriterionRow
                      key={criterion.id}
                      criterion={criterion}
                      onEdit={() => {
                        setEditName(criterion.name);
                        setEditDescription(criterion.description ?? "");
                        setEditThreshold(JSON.stringify(criterion.threshold, null, 2));
                        setEditWeight(criterion.weight);
                        setEditingId(criterion.id);
                      }}
                      onDelete={() => handleDelete(criterion.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {editingId && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Weight</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={editWeight}
                    onChange={(e) => setEditWeight(parseFloat(e.target.value) || 1)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description (optional)</label>
                <Input
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="What this criterion measures"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Threshold (JSON)</label>
                <textarea
                  value={editThreshold}
                  onChange={(e) => setEditThreshold(e.target.value)}
                  className="w-full p-2 text-sm font-mono border rounded-md bg-background"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleUpdate(editingId)}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
